import React from 'react';
import {FaGamepad, FaUsers, FaMagic, FaTrophy} from 'react-icons/fa';
import * as FaIcons from 'react-icons/fa';
import Modal from '../common/Modal';
import ContentDates from '../common/ContentDates';
import {
  CHARACTERS,
  SPECIAL_BUBBLES,
  ACHIEVEMENT_CATEGORIES
} from '../../utils/gameData';

const HelpPanel = ({isOpen, onClose}) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="如何遊玩泡泡樂園？"
      size="lg"
    >
      <article
        className="space-y-8"
        itemScope
        itemType="https://schema.org/Article"
      >
        <meta itemProp="headline" content="如何遊玩泡泡樂園？" />
        <meta itemProp="author" content="Homy Tech Limited" />
        <ContentDates
          published="2024-01-01"
          updated="2024-01-20"
          className="text-center"
        />

        <section className="bg-gradient-to-br from-pink-50 to-purple-50 rounded-2xl p-6">
          <h3 className="flex items-center gap-3 text-2xl font-bold text-pink-600 mb-4">
            <span className="w-10 h-10 bg-pink-500 rounded-full flex items-center justify-center">
              <FaGamepad className="text-white" aria-hidden="true" />
            </span>
            遊戲的基本玩法是甚麼？
          </h3>
          <div className="space-y-3 text-gray-700">
            <p className="text-base leading-relaxed">
              🎯 <strong>目標：</strong>在時間內點擊泡泡角色，獲得最高分！
            </p>
            <p className="text-base leading-relaxed">
              ⚡ <strong>普通泡泡：</strong>點擊一次即可消除，獲得10分。
            </p>
            <p className="text-base leading-relaxed">
              💎 <strong>特殊泡泡：</strong>需要連續點擊3次才能消除，但提供豐厚獎勵！
            </p>
            <p className="text-base leading-relaxed">
              🔥 <strong>連擊系統：</strong>快速連續點擊可增加連擊倍率，最高可達3倍分數！
            </p>
            <p className="text-base leading-relaxed">
              🚀 <strong>等級系統：</strong>達到分數門檻後會升級，泡泡速度和數量會增加！
            </p>
          </div>
        </section>

        <section className="bg-gradient-to-br from-blue-50 to-cyan-50 rounded-2xl p-6">
          <h3 className="flex items-center gap-3 text-2xl font-bold text-blue-600 mb-4">
            <span className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
              <FaUsers className="text-white" aria-hidden="true" />
            </span>
            遊戲中有哪些泡泡角色？
          </h3>
          <p className="text-gray-700 mb-4">
            遊戲中會出現各種可愛的泡泡角色！
          </p>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-4">
            {CHARACTERS.map((character) => {
              const Icon = FaIcons[character.icon];

              return (
                <div
                  key={character.id}
                  className="flex flex-col items-center p-3 bg-white rounded-xl shadow-md hover:shadow-lg transition-shadow"
                >
                  <div
                    className="w-14 h-14 rounded-full flex items-center justify-center mb-2"
                    style={{backgroundColor: `${character.color}20`}}
                  >
                    <div
                      style={{
                        color: character.color,
                        fontSize: '28px'
                      }}
                    >
                      {Icon && <Icon aria-hidden="true" />}
                    </div>
                  </div>
                  <span className="text-xs font-bold text-gray-700 text-center">
                    {character.name}
                  </span>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-gradient-to-br from-yellow-50 to-orange-50 rounded-2xl p-6">
          <h3 className="flex items-center gap-3 text-2xl font-bold text-orange-600 mb-4">
            <span className="w-10 h-10 bg-orange-500 rounded-full flex items-center justify-center">
              <FaMagic className="text-white" aria-hidden="true" />
            </span>
            特殊泡泡有甚麼效果？
          </h3>
          <p className="text-gray-700 mb-4">
            特殊泡泡需要點擊3次才能消除，但會提供強大獎勵！
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {Object.values(SPECIAL_BUBBLES).map((bubble) => {
              const Icon = FaIcons[bubble.icon];

              return (
                <div
                  key={bubble.type}
                  className="flex items-center gap-4 p-4 bg-white rounded-xl shadow-md hover:shadow-lg transition-all"
                >
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{
                      backgroundColor: `${bubble.color}33`,
                      color: bubble.color
                    }}
                  >
                    {Icon && <Icon size={28} aria-hidden="true" />}
                  </div>
                  <div className="flex-1">
                    <h4
                      className="font-bold text-lg mb-1"
                      style={{color: bubble.color}}
                    >
                      {bubble.name}
                    </h4>
                    <p className="text-sm text-gray-600">
                      {bubble.effect === 'score' && `🌟 獲得${bubble.points}分！`}
                      {bubble.effect === 'time' && '⏰ 增加5秒遊戲時間'}
                      {bubble.effect === 'explode' && '💥 消除附近所有泡泡'}
                      {bubble.effect === 'combo' && '⚡ 激活2倍連擊加成10秒'}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-gradient-to-br from-purple-50 to-pink-50 rounded-2xl p-6">
          <h3 className="flex items-center gap-3 text-2xl font-bold text-purple-600 mb-4">
            <span className="w-10 h-10 bg-purple-500 rounded-full flex items-center justify-center">
              <FaTrophy className="text-white" aria-hidden="true" />
            </span>
            如何解鎖遊戲成就？
          </h3>
          <p className="text-gray-700 mb-4">
            完成指定挑戰即可解鎖25個以上的遊戲成就！
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {Object.entries(ACHIEVEMENT_CATEGORIES).map(([key, category]) => {
              const Icon = FaIcons[category.icon];

              return (
                <div
                  key={key}
                  className="p-4 bg-white rounded-xl shadow-md flex items-center gap-3"
                >
                  <div
                    className={`w-12 h-12 rounded-full flex items-center justify-center bg-${category.color}-100`}
                  >
                    {Icon && (
                      <Icon
                        className={`text-${category.color}-600`}
                        size={20}
                        aria-hidden="true"
                      />
                    )}
                  </div>
                  <div>
                    <h4 className="font-bold text-base text-gray-800">
                      {category.name}
                    </h4>
                    <p className="text-xs text-gray-600">
                      {category.description}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>
        </section>

        <section className="bg-gradient-to-br from-green-50 to-teal-50 rounded-2xl p-6">
          <h3 className="flex items-center gap-3 text-2xl font-bold text-green-600 mb-4">
            <span className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center">
              <FaGamepad className="text-white" aria-hidden="true" />
            </span>
            泡泡樂園有哪些遊戲模式？
          </h3>
          <div className="space-y-3">
            <div className="p-4 bg-white rounded-xl shadow-md">
              <h4 className="font-bold text-lg text-pink-600 mb-2">
                ⏱️ 時間挑戰
              </h4>
              <p className="text-gray-700">
                60秒內收集盡可能多的泡泡，追求最高分！
              </p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-md">
              <h4 className="font-bold text-lg text-purple-600 mb-2">
                ♾️ 無盡模式
              </h4>
              <p className="text-gray-700">
                無時間限制，泡泡速度逐漸加快，挑戰你的極限！
              </p>
            </div>
            <div className="p-4 bg-white rounded-xl shadow-md">
              <h4 className="font-bold text-lg text-blue-600 mb-2">
                🎯 精準模式
              </h4>
              <p className="text-gray-700">
                錯過泡泡會扣分，需要精準點擊，限時60秒！
              </p>
            </div>
          </div>
        </section>
      </article>
    </Modal>
  );
};

export default HelpPanel;